<template>
  <InfuseModal
    :title="t('cpms.batch.createModal.title')"
    :description="t('cpms.batch.createModal.createDescription')"
    @close="emit('close')"
  >
    <form id="create-batch-job" @submit.prevent="onCreateBatch()">
      <div class="flex max-h-[70vh] max-w-[900px] flex-col gap-6 overflow-y-auto pb-8">
        <!-- Step 1: Select charging stations. Hidden when the caller already provided a
             selector (e.g. from the overview's multiselect) — we just show the count. -->
        <div v-if="!presetSelection" class="flex flex-col gap-3">
          <h3 class="text-base font-semibold text-neutral-800">
            {{ t('cpms.batch.createModal.selectStations') }}
          </h3>
          <div class="max-h-[300px] overflow-y-auto rounded-md border border-neutral-200">
            <ChargingStationFirmwareTable
              :rows="chargingStationStore.items"
              :show-charging-station-vendor="showChargingStationVendor"
              :loading="chargingStationStore.loading"
              :has-more-items="!chargingStationStore.options.lastPage"
              :page="chargingStationStore.options.page"
              :search-enabled="true"
              :search-query="searchQuery"
              :enable-selection="true"
              @load="chargingStationStore.loadMore()"
              @search="onSearch"
              @selected="selectedStations = $event"
            />
          </div>
        </div>
        <div v-else class="flex flex-col gap-2">
          <button
            v-if="selectedStationIds.length"
            type="button"
            class="flex w-fit items-center gap-2 text-sm font-medium text-neutral-700 hover:text-primary-500"
            @click="presetChipsExpanded = !presetChipsExpanded"
          >
            <span>{{ t('cpms.batch.createModal.stationsSelected', { count: presetCount }) }}</span>
            <font-awesome-icon :icon="['far', presetChipsExpanded ? 'chevron-up' : 'chevron-down']" class="text-xs" />
          </button>
          <span v-else class="text-sm font-medium text-neutral-700">
            {{ t('cpms.batch.createModal.stationsSelected', { count: presetCount }) }}
          </span>
          <div v-if="presetChipsExpanded && selectedStationIds.length" class="flex flex-wrap gap-2">
            <span
              v-for="id in selectedStationIds"
              :key="id"
              class="rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1 text-xs text-neutral-700"
            >
              {{ id }}
            </span>
          </div>
        </div>

        <!-- Step 2: Batch type (only shown when multiple types are available) -->
        <div v-if="!props.batchType && batchTypeOptions.length > 1" class="flex flex-col gap-3">
          <h3 class="text-base font-semibold text-neutral-800">
            {{ t('cpms.batch.createModal.batchType') }}
          </h3>
          <InfuseSelect
            v-model="batchType"
            :options="batchTypeOptions"
            :placeholder="t('cpms.batch.createModal.batchType')"
            size="small"
            class="w-48"
            name="batch-type"
          />
        </div>

        <!-- Step 3: Firmware selection -->
        <div v-if="hasFirmwareBatch && batchType === 'FIRMWARE'" class="flex flex-col gap-3">
          <h3 class="text-base font-semibold text-neutral-800">
            {{ t('cpms.batch.createModal.selectFirmware') }}
          </h3>
          <!-- A firmware image serves one station group, so each group gets its own choice and its own
               job. A single group renders one unlabelled selector, as before. -->
          <FirmwareSelector
            v-if="!multipleGroups"
            v-model="firmwareByGroup[firmwareGroups[0].key]"
            @firmware-selected="(firmware?: Firmware) => onFirmwareSelected(firmwareGroups[0].key, firmware)"
            @url-changed="(url: string) => onUrlChanged(firmwareGroups[0].key, url)"
          />
          <template v-else>
            <p class="text-sm text-neutral-600">
              {{
                t('cpms.batch.createModal.firmwarePerGroup', {
                  chosen: chosenGroupCount,
                  total: firmwareGroups.length
                })
              }}
            </p>
            <div class="flex flex-col overflow-hidden rounded-md border border-neutral-200">
              <!-- Open rows stand out, so it is clear which types still block Create. -->
              <div
                v-for="group in firmwareGroups"
                :key="group.key"
                class="grid grid-cols-1 items-start gap-2 border-l-4 border-t border-t-neutral-100 p-3 first:border-t-0 transition-colors duration-150 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4"
                :class="
                  hasFirmwareChoice(firmwareByGroup[group.key])
                    ? 'border-l-success-500 bg-white'
                    : 'border-l-warning-500 bg-warning-50'
                "
              >
                <div class="flex flex-col gap-0.5 pt-1">
                  <span class="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                    <font-awesome-icon
                      v-if="hasFirmwareChoice(firmwareByGroup[group.key])"
                      :icon="['fas', 'circle-check']"
                      class="text-success-500"
                    />
                    {{ group.label || t('cpms.batch.createModal.unknownGroup') }}
                  </span>
                  <span class="text-xs text-neutral-500">
                    {{ t('cpms.batch.createModal.stationsSelected', { count: group.stationIds.length }) }}
                  </span>
                  <span v-if="!hasFirmwareChoice(firmwareByGroup[group.key])" class="text-xs font-medium text-warning-700">
                    {{ t('cpms.batch.createModal.firmwareRequired') }}
                  </span>
                </div>
                <FirmwareSelector
                  v-model="firmwareByGroup[group.key]"
                  @firmware-selected="(firmware?: Firmware) => onFirmwareSelected(group.key, firmware)"
                  @url-changed="(url: string) => onUrlChanged(group.key, url)"
                />
              </div>
            </div>
          </template>
          <InfuseField :label="t('cpms.batch.firmware.batchModal.retrieveDate')" size="small">
            <InfuseDatePicker
              v-model="retrieveDateTime"
              :placeholder="t('cpms.batch.firmware.batchModal.retrieveDatePlaceholder')"
              :min-date="new Date()"
              format="yyyy-MM-dd HH:mm:ss"
              enable-time-picker
              enable-seconds
              :auto-apply="true"
              :inline-with-input="true"
              teleport="body"
              input-class-name="dp-custom-input"
              class="w-full"
            />
          </InfuseField>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfuseField
              :label="t('cpms.batch.firmware.batchModal.numberOfRetries')"
              size="small"
              :help-text="t('cpms.batch.firmware.batchModal.numberOfRetriesHelpText')"
              optional
            >
              <InfuseInput
                v-model="numberOfRetries"
                type="number"
                size="small"
                :min="0"
                :placeholder="t('cpms.batch.firmware.batchModal.numberOfRetriesPlaceholder')"
              />
            </InfuseField>
            <InfuseField
              :label="t('cpms.batch.firmware.batchModal.retryInterval')"
              size="small"
              :help-text="t('cpms.batch.firmware.batchModal.retryIntervalHelpText')"
              optional
            >
              <InfuseInput
                v-model="retryIntervalSeconds"
                type="number"
                size="small"
                :min="0"
                :placeholder="t('cpms.batch.firmware.batchModal.retryIntervalPlaceholder')"
              />
            </InfuseField>
          </div>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="emit('close')">
        {{ t('action.cancel') }}
      </InfuseButton>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="create-batch-job"
        variant="primary"
        :disabled="!canCreate"
        :loading="isCreating"
      >
        {{ t('cpms.batch.createModal.createBatchJob') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Firmware } from '@infuse/api/cpms/firmware-management';
import { BatchJobType, FirmwareBatchJobCreateRequest, type StationSelector } from '@infuse/api/cpms/batch';
import { parseRetryAttempts } from '@infuse/cpms/batch/composables';
import {
  ChargingStation,
  groupStations,
  type StationGroup,
  useChargingStationStore
} from '@infuse/cpms/charging-station/store';
import { useChargingStationTypeStore } from '@infuse/cpms/config/store';
import { ChargingStationFirmwareTable } from '@infuse/cpms/charging-station/ui';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';
import { FirmwareSelector } from '@infuse/cpms/location/ui';
import {
  InfuseButton,
  InfuseDatePicker,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect
} from '@infuse/ui/components';
import { useNotify } from '@infuse/cpms/ui';
import { useI18n } from 'vue-i18n';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const { t } = useI18n();
const { notifySuccess, notifyFailure } = useNotify();
const showChargingStationVendor = import.meta.env.VITE_UI_VARIANT_SHOW_CHARGING_STATION_VENDOR !== 'false';
const chargingStationStore = useChargingStationStore();
const chargingStationTypeStore = useChargingStationTypeStore();
const featureStore = useFeatureStore();

interface Props {
  /** When provided, the station-selection table is hidden and this selector is used
   *  verbatim (the stations were already chosen upstream, e.g. the overview). */
  stationSelector?: StationSelector;
  /** Number of stations the selector targets — shown instead of the table. */
  selectionCount?: number;
  /** The preset selection split per station group; a firmware image serves one group.
   *  Empty or unset means "unknown" and is not enforced. */
  stationGroups?: StationGroup[];
  /** Batch type chosen upstream (bulk action menu). Hides the type picker. */
  batchType?: BatchJobType;
}
const props = withDefaults(defineProps<Props>(), {
  stationSelector: undefined,
  selectionCount: undefined,
  stationGroups: undefined,
  batchType: undefined
});

interface Emits {
  (e: 'close'): void;
  (e: 'created'): void;
}

const emit = defineEmits<Emits>();

const presetSelection = computed(() => props.stationSelector != null);
const presetChipsExpanded = ref(false);
const selectedStationIds = computed(() =>
  props.stationSelector?.type === 'ID' ? props.stationSelector.chargingStationIds : []
);
// Count shown for a preset selection: explicit count if given, else the id count.
const presetCount = computed(() => props.selectionCount ?? selectedStationIds.value.length);

const hasFirmwareBatch = computed(() => featureStore.hasFeature(Feature.BATCH_RUNNER_FIRMWARE));

const batchTypeOptions = computed(() => {
  const options = [];
  if (hasFirmwareBatch.value) {
    options.push({ key: t('cpms.batch.createModal.titleFirmware'), value: 'FIRMWARE' });
  }
  return options;
});

const batchType = ref<string>(props.batchType ?? BatchJobType.FIRMWARE);
const selectedStations = ref<ChargingStation[]>([]);
const searchQuery = ref('');
const isCreating = ref(false);

interface FirmwareChoice {
  firmware?: Firmware;
  customFirmwareUrl?: string;
  selectionMode: 'select' | 'url';
}

const newFirmwareChoice = (): FirmwareChoice => ({
  selectionMode: 'select',
  firmware: undefined,
  customFirmwareUrl: ''
});
const retrieveDateTime = ref<Date>(new Date(Date.now() + 24 * 60 * 60 * 1000));
const numberOfRetries = ref<number | undefined>(undefined);
const retryIntervalSeconds = ref<number | undefined>(undefined);


// Preset selections carry their groups (the rows may never have been loaded); a selection
// made in this modal's own table is grouped here.
const effectiveGroups = computed(() =>
  presetSelection.value ? (props.stationGroups ?? []) : groupStations(selectedStations.value)
);

// Nothing grouped yet (no pick, or a preset without groups): one group holding the whole selection.
const UNGROUPED = 'UNGROUPED';

const firmwareGroups = computed<StationGroup[]>(() =>
  effectiveGroups.value.length ? effectiveGroups.value : [{ key: UNGROUPED, label: '', stationIds: [] }]
);

const multipleGroups = computed(() => firmwareGroups.value.length > 1);

// Keyed by group so a choice survives re-grouping (e.g. picking more stations in the table).
const firmwareByGroup = ref<Record<string, FirmwareChoice>>({});

watch(
  firmwareGroups,
  (groups) => groups.forEach((group) => (firmwareByGroup.value[group.key] ??= newFirmwareChoice())),
  { immediate: true }
);

function hasFirmwareChoice(choice?: FirmwareChoice): boolean {
  return !!(choice?.firmware || choice?.customFirmwareUrl?.trim());
}

const chosenGroupCount = computed(
  () => firmwareGroups.value.filter((group) => hasFirmwareChoice(firmwareByGroup.value[group.key])).length
);

const canCreate = computed(() => {
  const hasStations = presetSelection.value
    ? props.stationSelector?.type === 'ID'
      ? selectedStationIds.value.length > 0 // id selector: count from the ids themselves
      : (props.selectionCount ?? 0) > 0 // search selector: rely on the provided count
    : selectedStations.value.length > 0;
  if (!hasStations || isCreating.value) return false;
  // Firmware is the only batch this modal can create.
  if (!hasFirmwareBatch.value || batchType.value !== BatchJobType.FIRMWARE) return false;
  // Every group needs its own image before anything is created: a half-done update is harder to reason about.
  return chosenGroupCount.value === firmwareGroups.value.length && !!retrieveDateTime.value;
});

function onFirmwareSelected(groupKey: string, firmware?: Firmware) {
  const choice = firmwareByGroup.value[groupKey];
  choice.firmware = firmware;
  if (firmware) {
    choice.customFirmwareUrl = '';
  }
}

function onUrlChanged(groupKey: string, url: string) {
  const choice = firmwareByGroup.value[groupKey];
  choice.customFirmwareUrl = url;
  if (url.trim()) {
    choice.firmware = undefined;
  }
}

function onSearch(query: string) {
  searchQuery.value = query;
  chargingStationStore.options.query = query;
  chargingStationStore.resetItems();
  chargingStationStore.search();
}

// The charging station store is shared with the stations page; restore its state on close.
let previousOptions: typeof chargingStationStore.options | undefined;

onMounted(async () => {
  // Selector preset upstream → no table, so don't touch the shared list store
  // (it's the same singleton the overview behind this modal is using).
  if (presetSelection.value) return;
  previousOptions = { ...chargingStationStore.options };
  chargingStationStore.resetOptions();
  chargingStationStore.options.rootId = undefined;
  await chargingStationTypeStore.getAllTypes();
  // Closed during the await: onUnmounted already restored the shared store, leave it.
  if (unmounted) return;
  chargingStationStore.search();
});

let unmounted = false;
onUnmounted(() => {
  unmounted = true;
  if (previousOptions) {
    chargingStationStore.options = previousOptions;
    chargingStationStore.resetItems();
    chargingStationStore.search();
  }
});

async function onCreateBatch() {
  if (!canCreate.value) return;

  isCreating.value = true;
  try {
    // Shared schedule and retry settings; only the firmware differs per group.
    const startAt = new Date().toISOString();
    const retrieveAt =
      retrieveDateTime.value?.toISOString() ?? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    const retryInterval = toRetryInterval(retryIntervalSeconds.value);
    const batchStore = (await import('@infuse/cpms/batch/store')).useBatchStore();

    // Sequential, so every group's outcome is known and a failure is never masked by a later success.
    const created: string[] = [];
    const failed: StationGroup[] = [];
    for (const group of firmwareGroups.value) {
      const choice = firmwareByGroup.value[group.key];
      const batchRequest: FirmwareBatchJobCreateRequest = {
        stationSelector: selectorForGroup(group),
        startAt,
        retrieveAt,
        maxRetryAttempts: parseRetryAttempts(numberOfRetries.value)
      };
      if (retryInterval) batchRequest.retryInterval = retryInterval;
      if (choice.firmware) batchRequest.firmwareId = choice.firmware.id;
      else batchRequest.location = choice.customFirmwareUrl?.trim();

      try {
        const response = await batchStore.createBatch(batchRequest);
        created.push(response.id);
      } catch (error) {
        console.error(`Failed to create firmware batch job for group ${group.key}:`, error);
        failed.push(group);
      }
    }

    notifyOutcome(created, failed);
    if (created.length) {
      emit('created');
      emit('close');
    }
  } finally {
    isCreating.value = false;
  }
}

function toRetryInterval(rawSeconds: number | undefined): string | undefined {
  if (rawSeconds === undefined || rawSeconds === null || `${rawSeconds}`.trim() === '') return undefined;
  const seconds = Number(rawSeconds);
  return Number.isFinite(seconds) && seconds >= 0 ? `PT${seconds}S` : undefined;
}

function notifyOutcome(created: string[], failed: StationGroup[]): void {
  if (failed.length && created.length) {
    // Some groups are already updating, so name the failed ones instead of implying nothing happened.
    notifyFailure({
      title: t('cpms.batch.firmware.batchJobPartial.title'),
      message: t('cpms.batch.firmware.batchJobPartial.message', {
        created: created.length,
        total: created.length + failed.length,
        groups: failed.map((group) => group.label || t('cpms.batch.createModal.unknownGroup')).join(', ')
      })
    });
    return;
  }
  if (failed.length) {
    notifyFailure({
      title: t('cpms.batch.firmware.batchJobError.title'),
      message: t('cpms.batch.firmware.batchJobError.message')
    });
    return;
  }
  notifySuccess({
    title: t('cpms.batch.firmware.batchJobCreated.title'),
    message:
      created.length > 1
        ? t('cpms.batch.firmware.batchJobsCreated.message', { count: created.length })
        : t('cpms.batch.firmware.batchJobCreated.message', { id: created[0] })
  });
}

// A grouped selection sends each group's own ids; only an unsplit one falls back to the whole selection.
function selectorForGroup(group: StationGroup): StationSelector {
  if (group.key !== UNGROUPED) {
    return { type: 'ID', chargingStationIds: group.stationIds };
  }
  return props.stationSelector ?? { type: 'ID', chargingStationIds: selectedStations.value.map((s) => s.id) };
}
</script>
